import React, { useState, useEffect, useRef } from 'react';
import {
  Send,
  Paperclip,
  Smile,
  MoreVertical,
  Calendar,
  CheckSquare,
  AlertCircle,
  User,
  Loader2,
  Package,
  PackageCheck,
  Truck,
  X,
  MapPin,
  Clock,
  GripVertical,
  FileCode,
  Monitor,
  ChevronRight,
  ChevronLeft,
  Mic,
  MicOff,
  Volume2,
  VolumeX
} from 'lucide-react';
import { onNewMessage, offNewMessage, sendMessage, getSocket } from '../services/socket';
import { chatWithAI, createTask, getTasksByDepartment, updateTask, getKnowledgeForAI } from '../services/api';
import api from '../services/api';
import ChatCalendar from './ChatCalendar';
import ScriptsManager from './ScriptsManager';
import DevicesManager from './DevicesManager';

export default function Chat({ department, user, allUsers = [] }) {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [tasks, setTasks] = useState([]);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Estado para sidebar redimensionable
  const [sidebarWidth, setSidebarWidth] = useState(320);
  const [isResizing, setIsResizing] = useState(false);
  const containerRef = useRef(null);

  // Detectar si es movil y ocultar sidebar
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [showMobileSidebar, setShowMobileSidebar] = useState(false);

  // Estado para colapsar sidebar derecho en desktop
  const [rightSidebarCollapsed, setRightSidebarCollapsed] = useState(false);

  // Estado para Scripts Manager y Devices Manager (solo Soporte)
  const [showScriptsManager, setShowScriptsManager] = useState(false);
  const [showDevicesManager, setShowDevicesManager] = useState(false);

  // Estado para reconocimiento de voz y síntesis de voz
  const [isListening, setIsListening] = useState(false);
  const [speechEnabled, setSpeechEnabled] = useState(true);
  const recognitionRef = useRef(null);
  const synthRef = useRef(window.speechSynthesis);
  const [availableVoices, setAvailableVoices] = useState([]);
  const [selectedVoice, setSelectedVoice] = useState(null);

  // Estado para drag & drop de archivos
  const [isDragging, setIsDragging] = useState(false);
  const [attachedFiles, setAttachedFiles] = useState([]);

  // Verificar si el usuario puede ver scripts (Soporte o superadmin)
  const canManageScripts = () => {
    if (user?.role === 'superadmin') return true;
    if (user?.user_departments) {
      return user.user_departments.some(ud =>
        ud.departments?.name?.toLowerCase().includes('soporte') ||
        ud.department_id === 'dept-2'
      );
    }
    return false;
  };

  // Cargar voces disponibles para síntesis de voz
  useEffect(() => {
    const loadVoices = () => {
      const voices = synthRef.current.getVoices();
      setAvailableVoices(voices);

      // Seleccionar voz española de alta calidad por defecto
      const spanishVoice = voices.find(v =>
        v.lang.startsWith('es') && (v.name.includes('Google') || v.name.includes('Microsoft') || v.localService === false)
      ) || voices.find(v => v.lang.startsWith('es'));

      if (spanishVoice) {
        setSelectedVoice(spanishVoice);
      }
    };

    loadVoices();
    // En algunos navegadores las voces se cargan de forma asíncrona
    if (synthRef.current.onvoiceschanged !== undefined) {
      synthRef.current.onvoiceschanged = loadVoices;
    }
  }, []);

  // Inicializar reconocimiento de voz
  useEffect(() => {
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = false;
      recognitionRef.current.interimResults = true;
      recognitionRef.current.lang = 'es-ES'; // Español por defecto

      recognitionRef.current.onresult = (event) => {
        const transcript = Array.from(event.results)
          .map(result => result[0].transcript)
          .join('');

        setNewMessage(transcript);

        // Si es resultado final, enviar automáticamente
        if (event.results[0].isFinal) {
          setIsListening(false);
        }
      };

      recognitionRef.current.onerror = (event) => {
        console.error('Error de reconocimiento de voz:', event.error);
        setIsListening(false);
      };

      recognitionRef.current.onend = () => {
        setIsListening(false);
      };
    }

    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.abort();
      }
    };
  }, []);

  // Función para hablar (Text-to-Speech)
  const speak = (text) => {
    if (!speechEnabled || !synthRef.current) return;

    // Cancelar cualquier síntesis en curso
    synthRef.current.cancel();

    const utterance = new SpeechSynthesisUtterance(text);

    // Configurar voz
    if (selectedVoice) {
      utterance.voice = selectedVoice;
    }

    utterance.rate = 1.0; // Velocidad normal
    utterance.pitch = 1.0; // Tono normal
    utterance.volume = 1.0; // Volumen máximo

    synthRef.current.speak(utterance);
  };

  // Función para iniciar/detener reconocimiento de voz
  const toggleListening = () => {
    if (!recognitionRef.current) {
      alert('Tu navegador no soporta reconocimiento de voz. Usa Chrome o Edge.');
      return;
    }

    if (isListening) {
      recognitionRef.current.stop();
      setIsListening(false);
    } else {
      try {
        recognitionRef.current.start();
        setIsListening(true);
      } catch (error) {
        console.error('Error al iniciar reconocimiento:', error);
      }
    }
  };

  // Función para detener síntesis de voz
  const stopSpeaking = () => {
    if (synthRef.current) {
      synthRef.current.cancel();
    }
  };

  // Handlers para Drag & Drop
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      // Filtrar tipos de archivo permitidos
      const allowedTypes = ['image/', 'application/pdf', 'text/', 'application/'];
      const validFiles = files.filter(file =>
        allowedTypes.some(type => file.type.startsWith(type))
      );

      if (validFiles.length > 0) {
        setAttachedFiles(prev => [...prev, ...validFiles]);

        // Mostrar mensaje de confirmación
        const fileNames = validFiles.map(f => f.name).join(', ');
        setNewMessage(prev =>
          prev ? `${prev}\n📎 Archivos adjuntos: ${fileNames}` : `📎 Archivos adjuntos: ${fileNames}`
        );
      }
    }
  };

  const removeAttachedFile = (index) => {
    setAttachedFiles(prev => prev.filter((_, i) => i !== index));
  };

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Estado para log de envios/recibos
  const [shipments, setShipments] = useState([]);
  const [showShipmentModal, setShowShipmentModal] = useState(false);
  const [shipmentType, setShipmentType] = useState('envio'); // 'envio' o 'recibo'
  const [shipmentForm, setShipmentForm] = useState({
    description: '',
    destination: '',
    quantity: '',
    tracking: '',
    notes: ''
  });

  // Estado para asignacion de eventos a otros usuarios
  const [pendingEventAssignment, setPendingEventAssignment] = useState(null);

  // Handlers para redimensionar sidebar
  const startResizing = (e) => {
    setIsResizing(true);
    e.preventDefault();
  };

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isResizing || !containerRef.current) return;
      const containerRect = containerRef.current.getBoundingClientRect();
      const newWidth = containerRect.right - e.clientX;
      // Limitar entre 200px y 500px
      if (newWidth >= 200 && newWidth <= 500) {
        setSidebarWidth(newWidth);
      }
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing]);

  // Nombre del usuario para mostrar
  const userName = user?.name?.split(' ')[0] || 'Usuario';

  // Cargar tareas del departamento
  useEffect(() => {
    loadTasks();
    loadShipments();
  }, [department.id]);

  const loadTasks = async () => {
    try {
      const response = await getTasksByDepartment(department.id);
      setTasks(response.data.tasks || []);
    } catch (error) {
      console.error('Error cargando tareas:', error);
    }
  };

  // Cargar log de envios/recibos
  const loadShipments = async () => {
    try {
      const response = await api.get(`/api/shipments/department/${department.id}`);
      setShipments(response.data.shipments || []);
    } catch (error) {
      console.log('Error cargando envios:', error);
    }
  };

  // Detectar comando de envio/recibo
  const detectShipmentCommand = (message) => {
    const lower = message.toLowerCase().trim();
    if (lower === 'envio' || lower === 'envío') return 'envio';
    if (lower === 'recibo' || lower === 'recibido') return 'recibo';
    return null;
  };

  // Guardar envio/recibo
  const handleSaveShipment = async () => {
    try {
      const shipmentData = {
        type: shipmentType,
        description: shipmentForm.description,
        destination: shipmentForm.destination,
        quantity: shipmentForm.quantity,
        tracking: shipmentForm.tracking,
        notes: shipmentForm.notes,
        department_id: department.id,
        created_by: user.id,
        created_by_name: user.name
      };

      await api.post('/api/shipments', shipmentData);

      // Mensaje de confirmacion en el chat
      const confirmMessage = {
        id: Date.now().toString(),
        content: shipmentType === 'envio'
          ? `📦 ENVÍO registrado: ${shipmentForm.description} → ${shipmentForm.destination}${shipmentForm.quantity ? ` (${shipmentForm.quantity})` : ''}`
          : `📥 RECIBO registrado: ${shipmentForm.description} de ${shipmentForm.destination}${shipmentForm.quantity ? ` (${shipmentForm.quantity})` : ''}`,
        is_ai: true,
        created_at: new Date().toISOString(),
        user: { name: 'Sistema', avatar_url: null },
        departmentId: department.id,
        isSystem: true
      };
      setMessages(prev => [...prev, confirmMessage]);

      // Reset y cerrar
      setShipmentForm({ description: '', destination: '', quantity: '', tracking: '', notes: '' });
      setShowShipmentModal(false);
      loadShipments();
    } catch (error) {
      console.error('Error guardando envio:', error);
    }
  };

  // Detectar si el mensaje contiene una tarea para alguien
  const detectTaskInMessage = (message) => {
    // Patrones: "tarea para Diana: ...", "crear tarea para Juan: ...", "@Diana tarea: ..."
    const patterns = [
      /(?:crear\s+)?tarea\s+para\s+(\w+)[:\s]+(.+)/i,
      /@(\w+)\s+tarea[:\s]+(.+)/i,
      /asignar\s+a\s+(\w+)[:\s]+(.+)/i
    ];

    for (const pattern of patterns) {
      const match = message.match(pattern);
      if (match) {
        return {
          assigneeName: match[1],
          taskTitle: match[2].trim()
        };
      }
    }
    return null;
  };

  // Crear tarea detectada
  const handleCreateTask = async (assigneeName, taskTitle) => {
    try {
      const taskData = {
        title: taskTitle,
        assigned_to_name: assigneeName,
        department_id: department.id,
        department_name: department.name,
        priority: 'media'
      };

      await createTask(taskData);
      await loadTasks(); // Recargar tareas

      return true;
    } catch (error) {
      console.error('Error creando tarea:', error);
      return false;
    }
  };

  // Detectar si el mensaje menciona asignar evento a otro usuario
  const detectTargetUser = (message) => {
    // Patrones: "agregarselo a Carlos", "para Juan de comercial", "ponlo a Maria", "añadelo a Pedro"
    const patterns = [
      /(?:agregarselo|ponerselo|añadirselo|asignarselo)\s+a\s+(\w+)(?:\s+(?:de|del?)\s+(\w+))?/i,
      /(?:agregalo|ponlo|añadelo|asignalo)\s+a\s+(\w+)(?:\s+(?:de|del?)\s+(\w+))?/i,
      /(?:para|a)\s+(\w+)(?:\s+(?:de|del?)\s+(\w+))?\s*$/i,
      /en\s+(?:el\s+)?calendario\s+(?:de|del?)\s+(\w+)(?:\s+(?:de|del?)\s+(\w+))?/i,
    ];

    for (const pattern of patterns) {
      const match = message.match(pattern);
      if (match) {
        return {
          name: match[1],
          department: match[2] || null
        };
      }
    }
    return null;
  };

  // Buscar usuario por nombre (fuzzy match)
  const findUserByName = (targetName, targetDept) => {
    if (!allUsers || allUsers.length === 0) return null;

    const normalizedTarget = targetName.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");

    // Buscar coincidencia exacta o parcial en nombre
    return allUsers.find(u => {
      const fullName = (u.name || '').toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
      const firstName = fullName.split(' ')[0];

      // Coincidir nombre
      const nameMatch = firstName === normalizedTarget || fullName.includes(normalizedTarget);

      // Si se especifico departamento, verificar tambien
      if (targetDept && u.department_name) {
        const deptName = u.department_name.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
        return nameMatch && deptName.includes(targetDept.toLowerCase());
      }

      return nameMatch;
    });
  };

  // Detectar evento de calendario en mensaje
  const detectCalendarEvent = (message) => {
    const lower = message.toLowerCase();

    // Patrones para detectar eventos de calendario
    const patterns = [
      // "hay que ir a cenar el dia 24" o "tenemos cena el 24"
      /(?:hay\s+que|tenemos|tengo)\s+(.+?)\s+(?:el\s+)?(?:dia\s+)?(\d{1,2})(?:\s+de\s+(\w+))?(?:\s+(?:por\s+la\s+)?(mañana|tarde|noche))?/i,
      // "añade en el calendario X el dia Y" / "añadir al calendario X el Y"
      /(?:añade|añadir|agrega|agregar|pon|poner)\s+(?:en\s+)?(?:el\s+)?calendario[:\s]+(.+?)\s+(?:el\s+)?(?:dia\s+)?(\d{1,2})(?:\s+de\s+(\w+))?(?:\s+a\s+las?\s+(\d{1,2})[:\.]?(\d{2})?)?/i,
      // "calendario: X el dia Y"
      /calendario[:\s]+(.+?)\s+(?:el\s+)?(?:dia\s+)?(\d{1,2})(?:\s+de\s+(\w+))?(?:\s+a\s+las?\s+(\d{1,2})[:\.]?(\d{2})?)?/i,
      // "evento: X el Y"
      /evento[:\s]+(.+?)\s+(?:el\s+)?(?:dia\s+)?(\d{1,2})(?:\s+de\s+(\w+))?(?:\s+a\s+las?\s+(\d{1,2})[:\.]?(\d{2})?)?/i,
      // "el dia X tengo/hay que Y"
      /(?:el\s+)?(?:dia\s+)?(\d{1,2})(?:\s+de\s+(\w+))?\s+(?:tengo|hay)\s+(?:que\s+)?(.+?)(?:\s+a\s+las?\s+(\d{1,2})[:\.]?(\d{2})?)?$/i,
    ];

    for (const pattern of patterns) {
      const match = message.match(pattern);
      if (match) {
        // Determinar que patron coincidio para extraer los datos correctamente
        let title, day, month, hour, minute, timeOfDay;

        if (pattern.source.startsWith('(?:hay\\s+que|tenemos|tengo)')) {
          // Patron "hay que ir a X el dia Y"
          title = match[1];
          day = match[2];
          month = match[3];
          timeOfDay = match[4]; // mañana, tarde, noche
        } else if (pattern.source.startsWith('(?:el\\s+)?(?:dia')) {
          // Patron "el dia X tengo Y"
          day = match[1];
          month = match[2];
          title = match[3];
          hour = match[4];
          minute = match[5];
        } else {
          title = match[1];
          day = match[2];
          month = match[3];
          hour = match[4];
          minute = match[5];
        }

        // Convertir mes en texto a numero
        const monthMap = {
          'enero': 0, 'febrero': 1, 'marzo': 2, 'abril': 3, 'mayo': 4, 'junio': 5,
          'julio': 6, 'agosto': 7, 'septiembre': 8, 'octubre': 9, 'noviembre': 10, 'diciembre': 11
        };

        const now = new Date();
        let eventMonth = month ? monthMap[month.toLowerCase()] : now.getMonth();
        let eventYear = now.getFullYear();

        // Si el mes ya paso, es del proximo año
        if (eventMonth !== undefined && eventMonth < now.getMonth()) {
          eventYear++;
        }
        if (eventMonth === undefined) eventMonth = now.getMonth();

        const eventDate = new Date(eventYear, eventMonth, parseInt(day));

        // Determinar hora por momento del dia
        let eventTime = '10:00';
        if (hour) {
          eventTime = `${hour.padStart(2, '0')}:${(minute || '00').padStart(2, '0')}`;
        } else if (timeOfDay) {
          switch(timeOfDay.toLowerCase()) {
            case 'mañana': eventTime = '10:00'; break;
            case 'tarde': eventTime = '17:00'; break;
            case 'noche': eventTime = '21:00'; break;
          }
        }

        // Limpiar titulo de partes de asignacion de usuario
        let cleanTitle = title.trim()
          .replace(/\s+(?:agregarselo|ponerselo|añadirselo|asignarselo|agregalo|ponlo|añadelo|asignalo)\s+a\s+\w+.*/i, '')
          .replace(/\s+(?:para|a)\s+\w+(?:\s+(?:de|del?)\s+\w+)?\s*$/i, '')
          .trim();

        // Detectar usuario destino
        const targetUser = detectTargetUser(message);

        return {
          title: cleanTitle,
          date: eventDate.toISOString().split('T')[0],
          time: eventTime,
          targetUser: targetUser
        };
      }
    }
    return null;
  };

  // Crear evento de calendario
  const handleCreateCalendarEvent = async (eventData, targetUserId = null) => {
    try {
      const newEvent = {
        title: eventData.title,
        date: eventData.date,
        time: eventData.time,
        duration: '60',
        department_id: department.id,
        created_by: user.id,
        created_by_name: user.name,
        assigned_to: targetUserId || user.id
      };

      await api.post('/api/events', newEvent);
      return true;
    } catch (error) {
      console.error('Error creando evento:', error);
      return false;
    }
  };

  // Confirmar asignacion de evento a otro usuario
  const handleConfirmEventAssignment = async (confirm) => {
    if (!pendingEventAssignment) return;

    const { eventData, targetUser } = pendingEventAssignment;

    if (confirm) {
      const eventCreated = await handleCreateCalendarEvent(eventData, targetUser.id);
      if (eventCreated) {
        const eventDate = new Date(eventData.date);
        const dateStr = eventDate.toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long' });
        const confirmMessage = {
          id: Date.now().toString(),
          content: `📅 Perfecto! He añadido "${eventData.title}" al calendario de ${targetUser.name} para el ${dateStr} a las ${eventData.time}.`,
          is_ai: true,
          created_at: new Date().toISOString(),
          user: { name: 'Xavia', avatar_url: null },
          departmentId: department.id
        };
        setMessages(prev => [...prev, confirmMessage]);
      }
    } else {
      // No confirmo, crear para el usuario actual
      const eventCreated = await handleCreateCalendarEvent(eventData, user.id);
      if (eventCreated) {
        const eventDate = new Date(eventData.date);
        const dateStr = eventDate.toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long' });
        const confirmMessage = {
          id: Date.now().toString(),
          content: `📅 Vale, lo he añadido a tu calendario: "${eventData.title}" para el ${dateStr} a las ${eventData.time}.`,
          is_ai: true,
          created_at: new Date().toISOString(),
          user: { name: 'Xavia', avatar_url: null },
          departmentId: department.id
        };
        setMessages(prev => [...prev, confirmMessage]);
      }
    }

    setPendingEventAssignment(null);
  };

  // Scroll al final cuando hay nuevos mensajes
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Escuchar nuevos mensajes via socket
  useEffect(() => {
    const handleNewMessage = (message) => {
      if (message.departmentId === department.id) {
        setMessages(prev => [...prev, message]);
      }
    };

    onNewMessage(handleNewMessage);

    // Cleanup
    return () => {
      offNewMessage(handleNewMessage);
    };
  }, [department.id]);

  // Cargar mensajes iniciales
  useEffect(() => {
    setMessages([
      {
        id: '1',
        content: `Hola ${userName}! Soy Xavia, tu asistente en ${department.name}. ¿En que puedo ayudarte?`,
        is_ai: true,
        created_at: new Date().toISOString(),
        user: { name: 'Xavia', avatar_url: null }
      }
    ]);
  }, [department.id, department.name, userName]);

  const handleSend = async () => {
    if (!newMessage.trim() || aiLoading) return;

    const userMessageContent = newMessage.trim();

    // Detectar comandos de envio/recibo
    const shipmentCommand = detectShipmentCommand(userMessageContent);
    if (shipmentCommand) {
      setShipmentType(shipmentCommand);
      setShowShipmentModal(true);
      setNewMessage('');
      setTimeout(() => inputRef.current?.focus(), 50);
      return;
    }

    const message = {
      id: Date.now().toString(),
      content: userMessageContent,
      is_ai: false,
      created_at: new Date().toISOString(),
      user: {
        id: user.id,
        name: user.name,
        avatar_url: user.avatar_url
      },
      departmentId: department.id
    };

    // Agregar mensaje localmente
    setMessages(prev => [...prev, message]);
    setNewMessage('');
    setTimeout(() => inputRef.current?.focus(), 50);

    // Enviar via socket
    sendMessage(department.id, message);

    // Detectar si es un evento de calendario
    const calendarEvent = detectCalendarEvent(userMessageContent);
    if (calendarEvent) {
      // Verificar si hay usuario destino
      if (calendarEvent.targetUser) {
        const foundUser = findUserByName(calendarEvent.targetUser.name, calendarEvent.targetUser.department);
        if (foundUser && foundUser.id !== user.id) {
          // Preguntar confirmacion
          setPendingEventAssignment({ eventData: calendarEvent, targetUser: foundUser });
          const eventDate = new Date(calendarEvent.date);
          const dateStr = eventDate.toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long' });
          const askMessage = {
            id: (Date.now() + 1).toString(),
            content: `📅 ¿Quieres que añada "${calendarEvent.title}" al calendario de ${foundUser.name} para el ${dateStr} a las ${calendarEvent.time}?`,
            is_ai: true,
            created_at: new Date().toISOString(),
            user: { name: 'Xavia', avatar_url: null },
            departmentId: department.id,
            isConfirmation: true
          };
          setMessages(prev => [...prev, askMessage]);
          return;
        }
      }

      // Crear evento para el usuario actual
      const eventCreated = await handleCreateCalendarEvent(calendarEvent);
      if (eventCreated) {
        const eventDate = new Date(calendarEvent.date);
        const dateStr = eventDate.toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long' });
        const confirmMessage = {
          id: (Date.now() + 1).toString(),
          content: `📅 He añadido al calendario: "${calendarEvent.title}" para el ${dateStr} a las ${calendarEvent.time}. Puedes verlo en el panel de la derecha.`,
          is_ai: true,
          created_at: new Date().toISOString(),
          user: { name: 'Xavia', avatar_url: null },
          departmentId: department.id
        };
        setMessages(prev => [...prev, confirmMessage]);
        return;
      }
    }

    // Detectar confirmacion de asignacion pendiente
    if (pendingEventAssignment) {
      const lower = userMessageContent.toLowerCase();
      if (lower.includes('si') || lower.includes('sí') || lower.includes('ok') || lower.includes('vale') || lower.includes('confirma')) {
        await handleConfirmEventAssignment(true);
        return;
      } else if (lower.includes('no') || lower.includes('cancelar') || lower.includes('para mi')) {
        await handleConfirmEventAssignment(false);
        return;
      }
    }

    // Detectar si es una tarea
    const taskDetected = detectTaskInMessage(userMessageContent);
    if (taskDetected) {
      const taskCreated = await handleCreateTask(taskDetected.assigneeName, taskDetected.taskTitle);
      if (taskCreated) {
        // Mensaje de confirmación de tarea
        const taskConfirmMessage = {
          id: (Date.now() + 1).toString(),
          content: `He creado la tarea "${taskDetected.taskTitle}" para ${taskDetected.assigneeName}. La podras ver en el panel de tareas.`,
          is_ai: true,
          created_at: new Date().toISOString(),
          user: { name: 'Xavia', avatar_url: null },
          departmentId: department.id
        };
        setMessages(prev => [...prev, taskConfirmMessage]);
        return; // No llamar a la IA para este mensaje
      }
    }

    // Obtener respuesta de Xavia
    setAiLoading(true);
    try {
      // Obtener conocimiento relevante para este departamento
      let knowledgeContext = '';
      try {
        const knowledgeResponse = await getKnowledgeForAI(department.id);
        if (knowledgeResponse.data.context) {
          knowledgeContext = `\n\nBase de conocimiento disponible:\n${knowledgeResponse.data.context}`;
        }
      } catch (err) {
        console.log('No se pudo cargar conocimiento:', err);
      }

      // Contexto específico del departamento
      const departmentContext = `Estas respondiendo en el chat del departamento de ${department.name}.
      Descripcion del departamento: ${department.description || department.name}.
      Adapta tus respuestas al contexto de este departamento.
      ${knowledgeContext}

      IMPORTANTE: Si la pregunta del usuario se relaciona con la base de conocimiento, usa esa informacion para responder.`;

      // Construir historial de conversación para contexto
      const conversationHistory = messages.slice(-5).map(m => ({
        role: m.is_ai ? 'assistant' : 'user',
        content: m.content
      }));

      const response = await chatWithAI(userMessageContent, conversationHistory, departmentContext);

      const aiMessage = {
        id: (Date.now() + 1).toString(),
        content: response.data.response,
        is_ai: true,
        created_at: new Date().toISOString(),
        user: { name: 'Xavia', avatar_url: null },
        departmentId: department.id
      };

      setMessages(prev => [...prev, aiMessage]);

      // XIA habla su respuesta
      if (speechEnabled) {
        // Limpiar markdown y emojis para mejor síntesis
        const cleanText = response.data.response
          .replace(/\*\*/g, '')
          .replace(/\*/g, '')
          .replace(/#{1,6}\s/g, '')
          .replace(/!\[.*?\]\(.*?\)/g, '') // Quitar imágenes markdown
          .replace(/\[([^\]]+)\]\(.*?\)/g, '$1') // Convertir links a texto
          .substring(0, 500); // Limitar longitud para no hablar demasiado
        speak(cleanText);
      }
    } catch (error) {
      console.error('Error al obtener respuesta de Xavia:', error);
      // Determinar mensaje de error apropiado
      let errorContent = 'Lo siento, no pude procesar tu mensaje. Intenta de nuevo.';
      if (error.response?.data?.error) {
        if (error.response.data.error.includes('no configurada')) {
          errorContent = 'Mi conexion de IA no esta configurada todavia. Contacta con el administrador para activarla.';
        } else if (error.response.status === 429) {
          errorContent = 'Estoy recibiendo muchas peticiones. Espera unos segundos e intenta de nuevo.';
        } else {
          errorContent = `Error: ${error.response.data.error}`;
        }
      }
      // Mostrar error como mensaje de sistema
      const errorMessage = {
        id: (Date.now() + 1).toString(),
        content: errorContent,
        is_ai: true,
        created_at: new Date().toISOString(),
        user: { name: 'Xavia', avatar_url: null },
        departmentId: department.id
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setAiLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return 'Hoy';
    } else if (date.toDateString() === yesterday.toDateString()) {
      return 'Ayer';
    } else {
      return date.toLocaleDateString('es-ES', { day: 'numeric', month: 'long' });
    }
  };

  return (
    <div ref={containerRef} className={`h-full flex bg-white ${isMobile ? 'rounded-none' : 'rounded-2xl'} shadow-sm border border-gray-100 overflow-hidden ${isResizing ? 'select-none' : ''}`}>
      {/* Main Chat Area */}
      <div className="flex-1 flex flex-col min-w-0 relative">
        {/* Messages */}
        <div className={`flex-1 overflow-y-auto ${isMobile ? 'p-3' : 'p-6'} space-y-${isMobile ? '3' : '4'}`}>
          {messages.map((message, index) => {
            const isOwn = message.user?.id === user.id;
            const isAI = message.is_ai;

            return (
              <div
                key={message.id}
                className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}
              >
                <div className={`flex items-end max-w-[85%] md:max-w-[70%] ${isOwn ? 'flex-row-reverse' : ''}`}>
                  {/* Avatar */}
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-medium flex-shrink-0 ${
                    isAI ? 'bg-[#3B82F6]' :
                    isOwn ? 'bg-[#3B82F6]' : 'bg-[#6B7280]'
                  } ${isOwn ? 'ml-2' : 'mr-2'}`}>
                    {isAI ? 'X' : message.user?.name?.charAt(0) || 'U'}
                  </div>

                  {/* Message Bubble */}
                  <div className={`rounded-2xl px-4 py-3 ${
                    isAI ? 'bg-[#F9FAFB] border border-gray-200' :
                    isOwn ? 'bg-[#3B82F6] text-white' : 'bg-[#F9FAFB]'
                  }`}>
                    {!isOwn && (
                      <p className={`text-xs font-medium mb-1 ${isAI ? 'text-[#6B7280]' : 'text-[#6B7280]'}`}>
                        {isAI ? 'XAVIA' : message.user?.name}
                      </p>
                    )}
                    <p className={`text-sm whitespace-pre-wrap ${isOwn && !isAI ? 'text-white' : 'text-[#111827]'}`}>
                      {message.content}
                    </p>
                    <p className={`text-xs mt-1.5 ${isOwn ? 'text-white/70' : 'text-[#6B7280]'}`}>
                      {formatTime(message.created_at)}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}

          {/* Loading indicator */}
          {aiLoading && (
            <div className="flex justify-start">
              <div className="flex items-end max-w-[85%] md:max-w-[70%]">
                <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-medium flex-shrink-0 bg-[#3B82F6] mr-2">
                  X
                </div>
                <div className="rounded-2xl px-4 py-3 bg-[#F9FAFB] border border-gray-200">
                  <p className="text-xs font-medium mb-1 text-[#6B7280]">Xavia</p>
                  <div className="flex items-center text-sm text-[#6B7280]">
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    Escribiendo...
                  </div>
                </div>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Input Area con Drag & Drop */}
        <div
          className={`border-t border-gray-100 p-3 md:p-4 transition-all duration-200 ${
            isDragging ? 'bg-blue-50 border-t-2 border-t-blue-400' : ''
          }`}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
        >
          {/* Indicador de drop */}
          {isDragging && (
            <div className="absolute inset-0 bg-blue-100/80 flex items-center justify-center z-10 rounded-xl border-2 border-dashed border-blue-400">
              <div className="text-center">
                <Paperclip className="w-12 h-12 text-blue-500 mx-auto mb-2" />
                <p className="text-blue-700 font-medium">Suelta los archivos aquí</p>
              </div>
            </div>
          )}

          {/* Archivos adjuntos */}
          {attachedFiles.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-3">
              {attachedFiles.map((file, index) => (
                <div
                  key={index}
                  className="flex items-center bg-blue-50 text-blue-700 px-3 py-1.5 rounded-lg text-sm"
                >
                  <Paperclip className="w-4 h-4 mr-1.5" />
                  <span className="truncate max-w-[150px]">{file.name}</span>
                  <button
                    onClick={() => removeAttachedFile(index)}
                    className="ml-2 hover:text-red-500 transition-colors"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}

          <div className="flex items-end space-x-2">
            {/* Boton para mostrar sidebar en movil */}
            {isMobile && (
              <button
                onClick={() => setShowMobileSidebar(true)}
                className="p-2 hover:bg-gray-50 rounded-lg text-[#6B7280] transition-colors duration-200"
                title="Ver calendario y tareas"
              >
                <Calendar className="w-5 h-5" />
              </button>
            )}
            <button className="p-2 hover:bg-gray-50 rounded-lg text-[#6B7280] hidden md:block transition-colors duration-200">
              <Paperclip className="w-5 h-5" />
            </button>
            {canManageScripts() && (
              <>
                <button
                  onClick={() => setShowScriptsManager(true)}
                  className="p-2 hover:bg-gray-50 rounded-lg text-[#6B7280] hidden md:block transition-colors duration-200"
                  title="Scripts de reparacion"
                >
                  <FileCode className="w-5 h-5" />
                </button>
                <button
                  onClick={() => setShowDevicesManager(true)}
                  className="p-2 hover:bg-gray-50 rounded-lg text-[#6B7280] hidden md:block transition-colors duration-200"
                  title="Ver taquillas"
                >
                  <Monitor className="w-5 h-5" />
                </button>
              </>
            )}
            {/* Botón de micrófono */}
            <button
              onClick={toggleListening}
              className={`p-2 rounded-lg transition-all duration-200 ${
                isListening
                  ? 'bg-red-500 text-white animate-pulse'
                  : 'hover:bg-gray-50 text-[#6B7280]'
              }`}
              title={isListening ? 'Detener grabación' : 'Hablar con XIA'}
            >
              {isListening ? <MicOff className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
            </button>

            {/* Botón de activar/desactivar voz de XIA */}
            <button
              onClick={() => {
                if (speechEnabled) {
                  stopSpeaking();
                }
                setSpeechEnabled(!speechEnabled);
              }}
              className={`p-2 rounded-lg transition-all duration-200 ${
                speechEnabled
                  ? 'hover:bg-gray-50 text-[#3B82F6]'
                  : 'hover:bg-gray-50 text-[#6B7280]'
              }`}
              title={speechEnabled ? 'Silenciar voz de XIA' : 'Activar voz de XIA'}
            >
              {speechEnabled ? <Volume2 className="w-5 h-5" /> : <VolumeX className="w-5 h-5" />}
            </button>

            <div className="flex-1 relative">
              <textarea
                ref={inputRef}
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSend();
                  }
                }}
                onPaste={(e) => {
                  // Permitir paste explícitamente
                  const pastedText = e.clipboardData.getData('text');
                  if (pastedText) {
                    e.preventDefault();
                    const start = e.target.selectionStart;
                    const end = e.target.selectionEnd;
                    const newText = newMessage.substring(0, start) + pastedText + newMessage.substring(end);
                    setNewMessage(newText);
                    // Mover cursor después del texto pegado
                    setTimeout(() => {
                      e.target.selectionStart = e.target.selectionEnd = start + pastedText.length;
                    }, 0);
                  }
                }}
                placeholder={isListening ? '🎤 Escuchando...' : 'Escribe un mensaje o usa el micrófono...'}
                className={`w-full px-4 py-3 bg-[#F9FAFB] border rounded-xl resize-none focus:outline-none focus:border-[#3B82F6] transition-colors duration-200 text-[#111827] placeholder:text-[#6B7280] ${
                  isListening ? 'border-red-300 bg-red-50' : 'border-gray-200'
                }`}
                rows={1}
                style={{ minHeight: '48px', maxHeight: '120px' }}
              />
            </div>
            <button
              onClick={handleSend}
              disabled={!newMessage.trim() || isListening}
              className="p-3 bg-[#3B82F6] text-white rounded-xl hover:bg-[#2563EB] disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-200"
            >
              <Send className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Resizable Divider - solo en desktop y cuando no está colapsado */}
      {!isMobile && !rightSidebarCollapsed && (
        <div
          onMouseDown={startResizing}
          className={`w-1 bg-gray-100 hover:bg-gray-300 cursor-col-resize flex items-center justify-center transition-colors duration-200 ${isResizing ? 'bg-[#3B82F6]' : ''}`}
        >
          <div className="w-4 h-8 flex items-center justify-center">
            <GripVertical className="w-3 h-3 text-[#6B7280]" />
          </div>
        </div>
      )}

      {/* Overlay para cerrar sidebar en movil */}
      {isMobile && showMobileSidebar && (
        <div
          className="fixed inset-0 bg-black/40 z-40"
          onClick={() => setShowMobileSidebar(false)}
        />
      )}

      {/* Botón de toggle del sidebar derecho (solo desktop) */}
      {!isMobile && !rightSidebarCollapsed && (
        <button
          onClick={() => setRightSidebarCollapsed(true)}
          className="absolute top-4 right-4 z-10 p-2 bg-white border border-gray-200 rounded-lg shadow-sm hover:bg-gray-50 transition-all duration-200"
          title="Ocultar panel derecho"
        >
          <ChevronRight className="w-4 h-4 text-[#6B7280]" />
        </button>
      )}

      {/* Botón para expandir cuando está colapsado */}
      {!isMobile && rightSidebarCollapsed && (
        <button
          onClick={() => setRightSidebarCollapsed(false)}
          className="fixed top-4 right-4 z-50 p-2 bg-white border border-gray-200 rounded-lg shadow-sm hover:bg-gray-50 transition-all duration-200"
          title="Mostrar panel derecho"
        >
          <ChevronLeft className="w-4 h-4 text-[#6B7280]" />
        </button>
      )}

      {/* Right Sidebar - Calendar & Tasks */}
      <div
        style={isMobile || rightSidebarCollapsed ? {} : { width: sidebarWidth }}
        className={`bg-[#F9FAFB] flex-shrink-0 overflow-y-auto transition-all duration-300
          ${isMobile
            ? `fixed inset-y-0 right-0 z-50 w-80 max-w-[85vw] transform transition-transform duration-300 ${showMobileSidebar ? 'translate-x-0' : 'translate-x-full'}`
            : rightSidebarCollapsed
              ? 'w-0 opacity-0 overflow-hidden'
              : ''
          }`}
      >
        {/* Boton cerrar en movil */}
        {isMobile && showMobileSidebar && (
          <div className="sticky top-0 bg-[#F9FAFB] p-3 border-b border-gray-200 flex justify-between items-center z-10">
            <span className="font-medium text-[#111827]">Calendario y Tareas</span>
            <button
              onClick={() => setShowMobileSidebar(false)}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors duration-200"
            >
              <X className="w-5 h-5 text-[#6B7280]" />
            </button>
          </div>
        )}
        {/* Bloques en flex-wrap - cuando hay espacio se ponen lado a lado */}
        <div className="flex flex-wrap">
          {/* Calendar Block */}
          <div className="p-4 border-b border-r border-gray-200" style={{ minWidth: '200px', flex: '1 1 200px' }}>
            <ChatCalendar department={department} user={user} />
          </div>

          {/* Tasks Block */}
          <div className="p-4 border-b border-gray-200" style={{ minWidth: '200px', flex: '1 1 200px' }}>
            <h3 className="text-xs font-medium text-[#6B7280] flex items-center mb-3">
              <CheckSquare className="w-3 h-3 mr-1.5" />
              Tareas ({tasks.filter(t => t.status !== 'completada').length})
            </h3>
            <div className="space-y-2 max-h-40 overflow-y-auto">
              {tasks.filter(t => t.status !== 'completada').length === 0 ? (
                <div className="bg-white rounded-xl p-3 border border-gray-200">
                  <p className="text-xs text-[#6B7280]">No hay tareas pendientes</p>
                </div>
              ) : (
                tasks.filter(t => t.status !== 'completada').map(task => (
                  <div key={task.id} className="bg-white rounded-xl p-3 border border-gray-200">
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium text-[#111827] truncate">{task.title}</p>
                        <p className="text-xs text-[#6B7280] truncate">
                          {task.assigned_to_name}
                        </p>
                      </div>
                      <button
                        onClick={async () => {
                          await updateTask(task.id, { status: 'completada' });
                          loadTasks();
                        }}
                        className="ml-1.5 p-1 text-[#3B82F6] hover:bg-[#3B82F6]/10 rounded-lg transition-colors duration-200"
                        title="Completar"
                      >
                        <CheckSquare className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Incidents Block (Solo Soporte) */}
          {department.name === 'Soporte Tecnico' && (
            <div className="p-4 border-b border-r border-gray-200" style={{ minWidth: '200px', flex: '1 1 200px' }}>
              <h3 className="text-xs font-medium text-[#6B7280] flex items-center mb-3">
                <AlertCircle className="w-3 h-3 mr-1.5 text-[#6B7280]" />
                Incidencias
              </h3>
              <div className="bg-white rounded-xl p-3 border border-gray-200">
                <p className="text-xs text-[#6B7280]">No hay incidencias</p>
              </div>
            </div>
          )}

          {/* Shipments Block (Solo Soporte Tecnico) */}
          {department.name === 'Soporte Tecnico' && (
            <div className="p-4 border-b border-gray-200" style={{ minWidth: '200px', flex: '1 1 200px' }}>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-xs font-medium text-[#6B7280] flex items-center">
                  <Truck className="w-3 h-3 mr-1.5 text-[#6B7280]" />
                  Envios/Recibos
                </h3>
                <div className="flex space-x-1">
                  <button
                    onClick={() => { setShipmentType('envio'); setShowShipmentModal(true); }}
                    className="p-1 text-[#6B7280] hover:bg-gray-100 rounded-lg transition-colors duration-200"
                    title="Nuevo envio"
                  >
                    <Package className="w-3.5 h-3.5" />
                  </button>
                  <button
                    onClick={() => { setShipmentType('recibo'); setShowShipmentModal(true); }}
                    className="p-1 text-[#6B7280] hover:bg-gray-100 rounded-lg transition-colors duration-200"
                    title="Nuevo recibo"
                  >
                    <PackageCheck className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
              <div className="space-y-2 max-h-32 overflow-y-auto">
                {shipments.length === 0 ? (
                  <div className="bg-white rounded-xl p-3 border border-gray-200">
                    <p className="text-xs text-[#6B7280]">No hay registros</p>
                  </div>
                ) : (
                  shipments.slice(0, 5).map(shipment => (
                    <div
                      key={shipment.id}
                      className="bg-white rounded-xl p-2.5 border border-gray-200 text-xs"
                    >
                      <div className="flex items-center">
                        {shipment.type === 'envio' ? (
                          <Package className="w-3.5 h-3.5 text-[#3B82F6] mr-1.5 flex-shrink-0" />
                        ) : (
                          <PackageCheck className="w-3.5 h-3.5 text-[#3B82F6] mr-1.5 flex-shrink-0" />
                        )}
                        <span className="truncate text-[#111827]">{shipment.description}</span>
                      </div>
                      <p className="text-[#6B7280] truncate ml-5">
                        {shipment.type === 'envio' ? '->' : '<-'} {shipment.destination}
                      </p>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modal de Envio/Recibo */}
      {showShipmentModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 w-full max-w-md mx-4">
            <div className="flex items-center justify-between p-5 border-b border-gray-100">
              <h3 className="text-lg font-semibold text-[#111827] flex items-center">
                {shipmentType === 'envio' ? (
                  <>
                    <Package className="w-5 h-5 mr-2 text-[#3B82F6]" />
                    Registrar Envio
                  </>
                ) : (
                  <>
                    <PackageCheck className="w-5 h-5 mr-2 text-[#3B82F6]" />
                    Registrar Recibo
                  </>
                )}
              </h3>
              <button
                onClick={() => setShowShipmentModal(false)}
                className="p-2 hover:bg-gray-50 rounded-lg transition-colors duration-200"
              >
                <X className="w-5 h-5 text-[#6B7280]" />
              </button>
            </div>

            <div className="p-5 space-y-4">
              <div>
                <label className="block text-sm font-medium text-[#111827] mb-2">
                  Que {shipmentType === 'envio' ? 'envias' : 'recibes'}? *
                </label>
                <input
                  type="text"
                  value={shipmentForm.description}
                  onChange={(e) => setShipmentForm({ ...shipmentForm, description: e.target.value })}
                  className="w-full px-4 py-3 bg-[#F9FAFB] border border-gray-200 rounded-xl focus:outline-none focus:border-[#3B82F6] transition-colors duration-200 text-[#111827] placeholder:text-[#6B7280]"
                  placeholder="Ej: 2 monitores, 1 impresora..."
                  autoFocus
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#111827] mb-2">
                  {shipmentType === 'envio' ? 'A donde?' : 'De donde?'} *
                </label>
                <input
                  type="text"
                  value={shipmentForm.destination}
                  onChange={(e) => setShipmentForm({ ...shipmentForm, destination: e.target.value })}
                  className="w-full px-4 py-3 bg-[#F9FAFB] border border-gray-200 rounded-xl focus:outline-none focus:border-[#3B82F6] transition-colors duration-200 text-[#111827] placeholder:text-[#6B7280]"
                  placeholder="Ej: Hotel Marina, Cine Coslada..."
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#111827] mb-2">
                    Cantidad
                  </label>
                  <input
                    type="text"
                    value={shipmentForm.quantity}
                    onChange={(e) => setShipmentForm({ ...shipmentForm, quantity: e.target.value })}
                    className="w-full px-4 py-3 bg-[#F9FAFB] border border-gray-200 rounded-xl focus:outline-none focus:border-[#3B82F6] transition-colors duration-200 text-[#111827] placeholder:text-[#6B7280]"
                    placeholder="Ej: 2 cajas"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#111827] mb-2">
                    N Seguimiento
                  </label>
                  <input
                    type="text"
                    value={shipmentForm.tracking}
                    onChange={(e) => setShipmentForm({ ...shipmentForm, tracking: e.target.value })}
                    className="w-full px-4 py-3 bg-[#F9FAFB] border border-gray-200 rounded-xl focus:outline-none focus:border-[#3B82F6] transition-colors duration-200 text-[#111827] placeholder:text-[#6B7280]"
                    placeholder="Opcional"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#111827] mb-2">
                  Notas adicionales
                </label>
                <textarea
                  value={shipmentForm.notes}
                  onChange={(e) => setShipmentForm({ ...shipmentForm, notes: e.target.value })}
                  className="w-full px-4 py-3 bg-[#F9FAFB] border border-gray-200 rounded-xl focus:outline-none focus:border-[#3B82F6] transition-colors duration-200 text-[#111827] placeholder:text-[#6B7280]"
                  rows={2}
                  placeholder="Cualquier informacion adicional..."
                />
              </div>
            </div>

            <div className="flex justify-end space-x-3 p-5 border-t border-gray-100">
              <button
                onClick={() => setShowShipmentModal(false)}
                className="px-5 py-2.5 text-[#6B7280] hover:text-[#111827] hover:bg-gray-50 rounded-xl transition-colors duration-200"
              >
                Cancelar
              </button>
              <button
                onClick={handleSaveShipment}
                disabled={!shipmentForm.description || !shipmentForm.destination}
                className="px-5 py-2.5 bg-[#3B82F6] text-white rounded-xl hover:bg-[#2563EB] transition-all duration-200 disabled:opacity-40"
              >
                {shipmentType === 'envio' ? 'Registrar Envio' : 'Registrar Recibo'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Scripts Manager Modal */}
      {showScriptsManager && (
        <ScriptsManager
          onClose={() => setShowScriptsManager(false)}
          onSelectScript={(script) => {
            // Insertar comando para ejecutar script en el chat
            const scriptCommand = `@script:${script.name} (ID: ${script.id})`;
            setNewMessage(scriptCommand);
            setShowScriptsManager(false);
          }}
        />
      )}

      {/* Devices Manager Modal */}
      {showDevicesManager && (
        <DevicesManager onClose={() => setShowDevicesManager(false)} />
      )}
    </div>
  );
}
